import { createReducer } from '@reduxjs/toolkit';

import {
  fetchPageByCategoryId,
  fetchPageBySlug,
  fetchPageByStoryId,
  fetchPageByTagId,
} from '../pages';
import { getPageComponentsPresetAssets } from '../helpers';
import {
  isAssetRestricted,
  isAssetExpired,
} from '../../shared/helpers/assetHelpers.js';
import { getCategoryMetadata } from '../../shared/helpers/categoryHelpers';
import { fetchSearchResults } from '../search/actions';

import {
  fetchSvpAssetById,
  fetchSvpAssets,
  fetchSvpAssetsByIds,
} from './actions';

import type { Vendor } from '../../types';
import type { CaseReducer } from '@reduxjs/toolkit/src/createReducer';
import type {
  SvpAssetAdditional,
  SvpAssetId,
} from '@vgtv/api-client/lib/svp_asset';

export * from './selectors';

export type AssetsState = Record<
  Vendor,
  Partial<Record<SvpAssetId, SvpAssetAdditional & { _embedded?: any }>>
>;

const initialState: AssetsState = { vgtv: {}, ab: {}, brandstudio: {} };

const storeAssetsFromPageComponentsPreset: CaseReducer<
  AssetsState,
  Pick<
    ReturnType<
      | typeof fetchPageByCategoryId.fulfilled
      | typeof fetchPageByTagId.fulfilled
      | typeof fetchPageByStoryId.fulfilled
      | typeof fetchPageBySlug.fulfilled
    >,
    'type' | 'payload'
  >
> = (state, action) => {
  if (action.payload) {
    const pageConfig = action.payload;

    const assets = getPageComponentsPresetAssets(pageConfig);

    assets.forEach((asset) => {
      updateAssetInStore(state, asset);
    });
  }
};

const getShouldUpdate = (
  asset: SvpAssetAdditional,
  storedAsset: SvpAssetAdditional
) => {
  if (asset.updated! > storedAsset.updated!) {
    return true;
  }

  if (asset.displays! > storedAsset.displays!) {
    return true;
  }

  const assetCategoryMetadata = getCategoryMetadata(asset.category);
  const storedAssetCategoryMetadata = getCategoryMetadata(storedAsset.category);

  // assets fetched from /search endpoint have no inherited category metadata,
  // so we should update stored asset if it got fetched from /assets/:assetId endpoint
  // and has greater number of metadata properties
  if (
    Object.keys(assetCategoryMetadata).length >
    Object.keys(storedAssetCategoryMetadata).length
  ) {
    return true;
  }

  return false;
};

function updateAssetInStore(state: AssetsState, asset: SvpAssetAdditional) {
  const storedAsset = state[asset.provider][asset.id];

  const shouldUpdate = !storedAsset || getShouldUpdate(asset, storedAsset);

  if (!isAssetRestricted(asset.provider, asset) && shouldUpdate) {
    state[asset.provider][asset.id] = asset;
  }
}

const assetsReducer = createReducer(initialState, (builder) =>
  builder
    .addCase(fetchSvpAssets.fulfilled, (state, action) => {
      const {
        _embedded: { assets },
      } = action.payload;

      (assets as (SvpAssetAdditional & { _embedded?: any })[]).forEach(
        (asset) => {
          // assets fetched from most-seen have no tags embedded, and would overwrite tags fetched from other places
          // TODO: see if https://github.schibsted.io/svp/platform/issues/417 is resolved (WONTFIX)
          const storedAsset = state[asset.provider][asset.id];
          if (
            storedAsset &&
            storedAsset._embedded &&
            asset._embedded === undefined
          ) {
            asset._embedded = storedAsset._embedded;
          }
          if (!isAssetExpired(asset)) {
            updateAssetInStore(state, asset);
          }
        }
      );
    })
    .addCase(fetchSvpAssetById.fulfilled, (state, action) => {
      const asset = action.payload;

      updateAssetInStore(state, asset);
    })
    .addCase(fetchSvpAssetsByIds.fulfilled, (state, action) => {
      const {
        _embedded: { assets },
      } = action.payload;

      assets.forEach((asset) => {
        if (!isAssetExpired(asset)) {
          updateAssetInStore(state, asset);
        }
      });
    })
    .addCase(
      fetchPageByCategoryId.fulfilled,
      storeAssetsFromPageComponentsPreset
    )
    .addCase(fetchPageByTagId.fulfilled, storeAssetsFromPageComponentsPreset)
    .addCase(fetchPageByStoryId.fulfilled, storeAssetsFromPageComponentsPreset)
    .addCase(fetchPageBySlug.fulfilled, storeAssetsFromPageComponentsPreset)
    .addCase(fetchSearchResults.fulfilled, (state, action) => {
      const { components } = action.payload;

      components.forEach((component) => {
        if (component.data.type === 'assets') {
          component.data.preset.forEach((asset) => {
            updateAssetInStore(state, asset);
          });
        }
      });
    })
);

export { assetsReducer, fetchSvpAssets, storeAssetsFromPageComponentsPreset };
